import { useEffect } from 'react';
import { CheckCircle, Clock, Info, WarningCircle } from 'shared/icons';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';

/**
 * A MOLDURA das telas do coletor — o que veio do protótipo do dono como
 * CONCEITO, redesenhado nos tokens do melidash (mono-*, success-*, danger-*,
 * Button, Mono).
 *
 * Três decisões do protótipo que valem a pena e estão aqui:
 *
 * 1. **Uma tela = um passo = uma ação.** Barra de progresso fina em cima, o
 *    conteúdo centrado numa coluna estreita, e UM botão primário grande no
 *    rodapé fixo. Nada de tela com três coisas para escolher: quem está de
 *    luva em cima de uma empilhadeira não decide, executa.
 *
 * 2. **Hierarquia tipográfica invertida.** O ENDEREÇO EM TEXTO CLARO é maior
 *    que o título da ação. O título diz o verbo ("Bipe a posição de origem"),
 *    o endereço diz o lugar ("Rack A, Bloco 10, Andar 3, Posição 1") e o código
 *    monoespaçado fica subordinado. Quem olha de longe precisa do LUGAR
 *    primeiro — o verbo ele já sabe, é sempre o mesmo trabalho.
 *
 * 3. **Texto = para onde ir; cartão monoespaçado = o que bipar.** Quando o
 *    código É a carga do passo (o STG do chão, o LPN esperado), ele sobe para
 *    um cartão com moldura em vez de ficar na linha discreta. A moldura é a
 *    pista visual de "este string aqui é o que o leitor tem que ler".
 *
 * O que NÃO veio: o layout, as cores e as fontes do protótipo (é conceito,
 * nunca spec de UI) e o vocabulário "série/serial" — no produto é LPN.
 */

/** Endereço em texto claro. É o maior elemento da tela, de propósito. */
export const PlainAddress = ({ children, className = '' }) => (
    <p className={`text-center text-3xl font-black leading-tight text-black dark:text-white ${className}`}>
        {children}
    </p>
);

/** Código subordinado ao endereço — identidade, não instrução. */
export const CodeLine = ({ children, className = '' }) => (
    <Mono className={`block text-center text-base text-mono-500 dark:text-mono-400 ${className}`}>{children}</Mono>
);

/**
 * O código que TEM que ser bipado neste passo. Moldura grossa = "leia isto".
 * `tone` acompanha o estado do passo (neutro enquanto espera, verde quando o
 * passo já casou).
 */
export const CodeCard = ({ children, caption = null, tone = 'neutral' }) => {
    const toneClasses =
        tone === 'success'
            ? 'border-success-600 bg-success-soft text-success-text dark:border-success-400 dark:bg-success-900/30 dark:text-success-300'
            : 'border-mono-300 bg-white text-black dark:border-mono-700 dark:bg-mono-950 dark:text-white';
    return (
        <div className={`rounded-2xl border-2 px-4 py-4 text-center ${toneClasses}`}>
            {caption ? <p className="mb-1 text-xs uppercase tracking-wide opacity-70">{caption}</p> : null}
            <Mono className="block break-all text-2xl font-black">{children}</Mono>
        </div>
    );
};

/**
 * Casca de um passo do fluxo guiado: progresso + corpo rolável + rodapé fixo.
 * `actions` é o rodapé; a PRIMEIRA ação é a primária e ocupa a largura toda.
 */
export const StepShell = ({ stepIndex = 0, stepCount = 0, stepName = null, title, children, footer = null }) => {
    const progress = stepCount > 1 ? Math.min(100, Math.max(0, (stepIndex / (stepCount - 1)) * 100)) : 0;

    return (
        <div className="flex min-h-[70vh] flex-col gap-4">
            {stepCount > 0 ? (
                <div className="space-y-1.5">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-mono-200 dark:bg-mono-800">
                        <div
                            className="h-full rounded-full bg-black transition-all duration-300 dark:bg-white"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    {stepName ? (
                        <p className="text-xs font-medium uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            {stepName}
                        </p>
                    ) : null}
                </div>
            ) : null}

            <div className="flex flex-1 flex-col justify-center gap-5">
                {title ? (
                    <h2 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">{title}</h2>
                ) : null}
                {children}
            </div>

            {footer ? <div className="sticky bottom-0 space-y-2 bg-transparent pt-2">{footer}</div> : null}
        </div>
    );
};

/**
 * Flash verde de tela cheia depois de cada bipe aceito. Some sozinho.
 * É overlay de tela INTEIRA (e não um toast) pelo mesmo motivo do protótipo:
 * tem que ser legível de relance, a metros de distância, sem procurar onde
 * apareceu.
 */
export const ScanFlash = ({ message, onDone, durationMs = 780 }) => {
    useEffect(() => {
        if (!message) {
            return undefined;
        }
        const timer = window.setTimeout(() => onDone?.(), durationMs);
        return () => window.clearTimeout(timer);
    }, [message, onDone, durationMs]);

    if (!message) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="assertive"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-success-600/95 px-6 text-center"
        >
            <p className="text-4xl font-black text-white">{message}</p>
        </div>
    );
};

/**
 * TELA VERMELHA (PRD §8.2 / §6.5). Mostra ESPERADO × LIDO lado a lado — a
 * pergunta do operador quando o bipe falha é sempre "o que eu bipei de
 * errado?", e responder isso é o que evita ele bipar a mesma etiqueta errada
 * três vezes. Nunca é terminal: sempre tem "tentar de novo".
 */
export const FailureScreen = ({ title, message = null, expected = null, read = null, reasons = [], actions = null, label }) => (
    <div className="flex min-h-[70vh] flex-col gap-5 rounded-2xl bg-danger-600 p-5 text-white">
        <div className="flex flex-1 flex-col justify-center gap-5">
            <WarningCircle size={64} className="mx-auto shrink-0" />
            <h2 className="text-center text-3xl font-black leading-tight">{title}</h2>
            {message ? <p className="text-center text-lg font-medium opacity-95">{message}</p> : null}

            {/* Regra do dono (02/09): a tela de erro NUNCA mostra o que o
                sistema esperava — só o que foi LIDO. `expected` continua aceito
                na assinatura por compatibilidade, mas não é renderizado. */}
            {read ? (
                <div className="rounded-xl bg-black/25 px-4 py-3 text-center">
                    <p className="text-xs uppercase tracking-wide opacity-80">{label('wms.collector.read')}</p>
                    <Mono className="block break-all text-2xl font-black">{read}</Mono>
                </div>
            ) : null}

            {reasons.length > 0 ? (
                <ul className="space-y-2">
                    {reasons.map((reason, index) => (
                        // A engine pode devolver a MESMA regra em dois motivos
                        // (dois ocupantes barrando o mesmo destino): a posição
                        // entra na chave para não colidir.
                        <li key={`${reason.rule}-${index}`} className="rounded-xl bg-white/15 px-4 py-3">
                            <p className="text-base font-semibold leading-snug">{reason.message}</p>
                            <Mono className="mt-1 block text-xs opacity-75">{reason.rule}</Mono>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
        {actions ? <div className="sticky bottom-0 space-y-2">{actions}</div> : null}
    </div>
);

/**
 * TELA DE ESPERA (M5, §6.5 — sincronia do par).
 *
 * Existe porque o coletor passou a ter um estado que o M4 não tinha: **nada
 * está errado e mesmo assim não dá para seguir**. Quem chega adiantado no par
 * LIFT+TRANSPORT não errou o bipe, não pegou o pallet errado e não precisa
 * "tentar de novo direito" — precisa esperar a outra metade largar a carga.
 *
 * Por isso ela é ÂMBAR e não vermelha. A tela vermelha do coletor tem um
 * contrato com o operador: "você fez algo que o sistema não aceita, olhe o
 * esperado × lido". Gastar essa cor com uma espera legítima é o jeito mais
 * rápido de ensinar o chão a ignorar a cor vermelha — e aí, no dia em que ela
 * significar mesmo um erro, ninguém lê.
 *
 * A moldura é a mesma da tela vermelha (ícone grande, título grande, corpo,
 * rodapé grudado embaixo) porque o operador já aprendeu esse formato no M4: o
 * que muda é a COR e o VERBO, não o lugar das coisas.
 */
export const WaitScreen = ({ title, message = null, children = null, actions = null }) => (
    <div className="flex min-h-[70vh] flex-col gap-5 rounded-2xl border-2 border-warning-400 bg-warning-soft p-5 dark:border-warning-600 dark:bg-warning-900/30">
        <div className="flex flex-1 flex-col justify-center gap-5">
            <Clock size={64} className="mx-auto shrink-0 animate-pulse text-warning-text dark:text-warning-300" />
            <h2 className="text-center text-3xl font-black leading-tight text-warning-text dark:text-warning-200">
                {title}
            </h2>
            {message ? (
                <p className="text-center text-lg font-medium text-warning-text dark:text-warning-300">{message}</p>
            ) : null}
            {children}
        </div>
        {actions ? <div className="sticky bottom-0 space-y-2">{actions}</div> : null}
    </div>
);

/**
 * Aviso de UMA linha no topo de uma lista — para o que é INFORMAÇÃO, não erro:
 * "outro operador aceitou primeiro", "você já tem uma OS ativa". São respostas
 * legítimas do sistema (o 409 do first-wins é a fila funcionando), e tratá-las
 * como falha faria o operador achar que o coletor está quebrado justo no
 * momento em que ele está funcionando como deveria.
 */
export const NoticeBanner = ({ tone = 'info', children, action = null }) => {
    const toneClasses =
        tone === 'warning'
            ? 'border-warning-400 bg-warning-soft text-warning-text dark:border-warning-700 dark:bg-warning-900/30 dark:text-warning-200'
            : 'border-info-400 bg-info-soft text-info-text dark:border-info-700 dark:bg-info-900/30 dark:text-info-200';
    const Icon = tone === 'warning' ? WarningCircle : Info;

    return (
        <div className={`space-y-3 rounded-xl border-2 px-4 py-3 ${toneClasses}`} role="status" aria-live="polite">
            <div className="flex items-start gap-2">
                <Icon size={22} className="mt-0.5 shrink-0" />
                <p className="text-base font-semibold leading-snug">{children}</p>
            </div>
            {action}
        </div>
    );
};

/** Tela verde de fim de movimento: o resumo do que aconteceu + próximo passo. */
export const SuccessScreen = ({ title, children, actions = null }) => (
    <div className="flex min-h-[70vh] flex-col gap-5 rounded-2xl border-2 border-success-600 bg-success-soft p-5 dark:border-success-500 dark:bg-success-900/25">
        <div className="flex flex-1 flex-col justify-center gap-5">
            <CheckCircle size={64} className="mx-auto shrink-0 text-success-700 dark:text-success-400" />
            <h2 className="text-center text-3xl font-black leading-tight text-success-text dark:text-success-200">
                {title}
            </h2>
            {children}
        </div>
        {actions ? <div className="sticky bottom-0 space-y-2">{actions}</div> : null}
    </div>
);

/** Botão primário do rodapé — alvo grande, largura total, para dedo com luva. */
export const BigAction = ({ children, className = '', ...props }) => (
    <Button size="lg" className={`w-full py-5 text-xl font-black ${className}`} {...props}>
        {children}
    </Button>
);

/**
 * Ação secundária DENTRO da tela vermelha. Não usa a variante `ghost` do
 * Button de propósito: ela traz `text-black dark:text-white`, e sobrepor com
 * `text-white` na className não é garantia — entre duas utilitárias de mesma
 * especificidade quem vence é a ordem no CSS gerado, não a ordem no atributo.
 * Sobre vermelho isso viraria texto preto em metade dos temas. Aqui as classes
 * são escritas uma vez, sem conflito.
 */
export const DangerScreenAction = ({ children, onClick, disabled = false }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full rounded-lg border border-white/40 px-6 py-3 text-lg font-semibold text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white disabled:cursor-not-allowed disabled:opacity-50"
    >
        {children}
    </button>
);
